/**
 * Hook para gerenciar configurações de UI com cache
 * Usa localStorage para cache local e fallback para valores hardcoded
 */

import { useState, useEffect, useCallback } from 'react'
import type { UIConfig } from '../types/settings'
import { getUIConfig } from '../services/settingsService'
import { 
  FOLDER_COLORS_FALLBACK,
  CATEGORY_COLORS_FALLBACK,
  ANNOTATION_COLORS_FALLBACK,
  ANNOTATION_CATEGORIES_FALLBACK,
  AUDIO_EXTENSIONS_FALLBACK,
  VIDEO_EXTENSIONS_FALLBACK
} from '../constants/uiConfigFallback.ts'

const CACHE_KEY = 'monkee-ui-config'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

interface CachedUIConfig {
  data: UIConfig
  timestamp: number
  tennant: string
  player?: string
}

interface UseUIConfigResult {
  data: UIConfig | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  clearCache: () => void
}

/**
 * Hook para buscar e gerenciar configurações de UI
 */
export function useUIConfig(
  tennant: string,
  player?: string,
  apiBaseUrl?: string
): UseUIConfigResult {
  const [data, setData] = useState<UIConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Carrega do cache local
  const loadFromCache = useCallback((): UIConfig | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null

      const parsed: CachedUIConfig = JSON.parse(cached)
      
      // Verifica se é o mesmo tenant/player
      if (parsed.tennant !== tennant || parsed.player !== player) {
        return null
      }

      // Verifica expiração
      const now = Date.now()
      if (now - parsed.timestamp > CACHE_DURATION) {
        return null
      }

      return parsed.data
    } catch (err) {
      console.error('Erro ao carregar cache:', err)
      return null
    }
  }, [tennant, player])

  // Salva no cache local
  const saveToCache = useCallback((config: UIConfig) => {
    try {
      const cached: CachedUIConfig = {
        data: config,
        timestamp: Date.now(),
        tennant,
        player,
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(cached))
    } catch (err) {
      console.error('Erro ao salvar cache:', err)
    }
  }, [tennant, player])

  // Limpa cache
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEY)
    } catch (err) {
      console.error('Erro ao limpar cache:', err)
    }
  }, [])

  // Fallback para valores hardcoded
  const getFallbackConfig = useCallback((): UIConfig => {
    console.warn('Usando configurações fallback (hardcoded)')
    return {
      categoryColors: CATEGORY_COLORS_FALLBACK,
      folderColors: FOLDER_COLORS_FALLBACK,
      annotationColors: ANNOTATION_COLORS_FALLBACK,
      annotationCategories: ANNOTATION_CATEGORIES_FALLBACK,
      audioExtensions: [
        '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', 
        '.wma', '.opus', '.aiff', '.ape', '.ac3', '.amr', '.wv', '.tta'
      ],
      videoExtensions: [
        '.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', 
        '.webm', '.m4v', '.3gp', '.mpg', '.mpeg', '.ogv', '.ts', '.vob', '.divx'
      ],
      audioMimeTypes: [
        'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/wave',
        'audio/x-wav', 'audio/ogg', 'audio/mp4', 'audio/x-m4a',
        'audio/aac', 'audio/flac', 'audio/x-flac', 'audio/x-ms-wma',
        'audio/opus', 'audio/aiff', 'audio/x-aiff'
      ],
      videoMimeTypes: [
        'video/mp4', 'video/mpeg', 'video/quicktime',
        'video/x-msvideo', 'video/x-matroska', 'video/webm',
        'video/x-ms-wmv', 'video/x-flv', 'video/3gpp',
        'video/ogg', 'video/MP2T'
      ],
      npsConfig: {
        npsEnabled: true,
        feedbackEnabled: true,
        frequencyDays: 90,
        triggerDelaySeconds: 3,
        triggerEvents: ['ANALYSIS_COMPLETE', 'EXPORT'],
        triggerOnFirstAnalysis: true,
      },
    }
  }, [])

  // Busca da API
  const fetchConfig = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Tenta carregar do cache primeiro
      const cachedConfig = loadFromCache()
      if (cachedConfig) {
        console.log('Configurações carregadas do cache local')
        setData(cachedConfig)
        setIsLoading(false)
        return
      }

      // Busca da API
      console.log(`Buscando configurações da API: tennant=${tennant}, player=${player || 'none'}`)
      const config = await getUIConfig(tennant, player, apiBaseUrl)
      
      // Salva no cache
      saveToCache(config)
      setData(config)
      
    } catch (err) {
      console.error('Erro ao buscar configurações:', err)
      setError(err instanceof Error ? err : new Error('Erro desconhecido'))
      
      // Usa fallback em caso de erro
      const fallbackConfig = getFallbackConfig()
      setData(fallbackConfig)
      
    } finally {
      setIsLoading(false)
    }
  }, [tennant, player, apiBaseUrl, loadFromCache, saveToCache, getFallbackConfig])

  // Efeito para carregar configurações
  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return {
    data,
    isLoading,
    error,
    refetch: fetchConfig,
    clearCache,
  }
}

/**
 * Hook simplificado para cores de categorias
 */
export function useCategoryColors(tennant: string, player?: string) {
  const { data, isLoading, error } = useUIConfig(tennant, player)
  
  return {
    categoryColors: data?.categoryColors ?? CATEGORY_COLORS_FALLBACK,
    isLoading,
    error,
  }
}

/**
 * Hook simplificado para cores de pastas
 */
export function useFolderColors(tennant: string, player?: string) {
  const { data, isLoading, error } = useUIConfig(tennant, player)
  
  return {
    folderColors: data?.folderColors ?? FOLDER_COLORS_FALLBACK,
    isLoading,
    error,
  }
}

/**
 * Hook simplificado para cores de anotações
 */
export function useAnnotationColors(tennant: string, player?: string) {
  const { data, isLoading, error } = useUIConfig(tennant, player)
  
  return {
    annotationColors: data?.annotationColors ?? ANNOTATION_COLORS_FALLBACK,
    isLoading,
    error,
  }
}

/**
 * Hook simplificado para categorias de anotações
 */
export function useAnnotationCategories(tennant: string, player?: string) {
  const { data, isLoading, error } = useUIConfig(tennant, player)
  
  return {
    annotationCategories: data?.annotationCategories ?? ANNOTATION_CATEGORIES_FALLBACK,
    isLoading,
    error,
  }
}

/**
 * Hook para extensões e MIME types de mídia
 */
export function useMediaExtensions(tennant: string, player?: string) {
  const { data, isLoading, error } = useUIConfig(tennant, player)
  
  return {
    audioExtensions: data?.audioExtensions ?? [
      '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'
    ],
    videoExtensions: data?.videoExtensions ?? [
      '.mp4', '.avi', '.mov', '.mkv', '.wmv', '.webm'
    ],
    audioMimeTypes: data?.audioMimeTypes ?? [
      'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4'
    ],
    videoMimeTypes: data?.videoMimeTypes ?? [
      'video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm'
    ],
    isLoading,
    error,
  }
}
